import { useState, useCallback, useEffect, useContext } from 'react'
import { useStore } from 'react-redux'
import useOffer from 'hooks/Offers/useOffer'
import CurrencyContext from 'contexts/currencyContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import {
  buildEnrichedMessage,
  buildOfferContextString,
  getOfferPropertyName,
  resolveOccupancyAgeThresholdsForSendMessage,
} from 'tripPlanner/assistant/utils/messageEnrichment'
import { isOccupancyThresholdOffer } from 'tripPlanner/assistant/utils/assistantOccupancyAgeThresholds'
import { getAllOffers } from 'selectors/offerSelectors'
import type { OrderLinkOptions, SubmitMessageOptions } from 'tripPlanner/assistant/runtime/chatStream'

export interface ComposerSubmitOptions extends SendMessageOptions, OrderLinkOptions {}

const NO_PREFILLED_SELECTION: Array<string> = []

interface UseMessageContextArgs {
  streamingSubmit: (message: string, options?: SubmitMessageOptions) => Promise<void>
  setFollowTail: (follow: boolean) => void
  setMessageInputValue: (value: string) => void
  focusChatInput?: () => void
  prefilledMultiChoiceSelection?: Array<string>
}

export function useMessageContext({
  streamingSubmit,
  setFollowTail,
  setMessageInputValue,
  focusChatInput,
  prefilledMultiChoiceSelection = NO_PREFILLED_SELECTION,
}: UseMessageContextArgs) {
  const currentCurrency = useContext(CurrencyContext)
  const store = useStore<App.State>()

  const [activeMultiChoiceSelection, setActiveMultiChoiceSelection] = useState<Array<string>>([])
  const [mobileSelectionText, setMobileSelectionText] = useState<string | undefined>(undefined)
  const [offerContextTag, setOfferContextTag] = useState<{ label: string; offerId: string } | undefined>(undefined)

  // Fetch offer data for context tag
  const [contextOfferData] = useOffer<App.AnyOffer>(offerContextTag?.offerId || '')

  // Update context tag label when offer data loads
  useEffect(() => {
    if (contextOfferData && offerContextTag) {
      const name = getOfferPropertyName(contextOfferData)
      if (name && offerContextTag.label !== name) {
        setOfferContextTag({ label: name, offerId: offerContextTag.offerId })
      }
    }
  }, [contextOfferData, offerContextTag])

  const handleSubmit = useCallback(async(message: string, options?: ComposerSubmitOptions) => {
    setMessageInputValue('')
    setFollowTail(false)

    const offerContext = offerContextTag && contextOfferData
      ? buildOfferContextString(contextOfferData, currentCurrency)
      : undefined
    const multiChoiceSelection = [...new Set([...prefilledMultiChoiceSelection, ...activeMultiChoiceSelection])]

    const occupancyOffer = options?.roomOption &&
      options.offerId &&
      offerContextTag?.offerId === options.offerId &&
      isOccupancyThresholdOffer(contextOfferData)
      ? contextOfferData
      : undefined

    const sendMessageOptions = options?.roomOption ? {
      ...options,
      occupancyAgeThresholds: options.occupancyAgeThresholds ??
        resolveOccupancyAgeThresholdsForSendMessage(options, getAllOffers(store.getState())),
    } : options

    const enrichedMessage = buildEnrichedMessage({
      message,
      multiChoiceSelection,
      sendMessageOptions,
      mobileSelectionText,
      offerContext,
      occupancyOffer,
    })

    // Clear state after building message
    if (offerContextTag) setOfferContextTag(undefined)
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)

    return streamingSubmit(enrichedMessage, { orderIds: options?.orderIds })
  }, [
    streamingSubmit,
    setFollowTail,
    setMessageInputValue,
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    contextOfferData,
    currentCurrency,
    prefilledMultiChoiceSelection,
    store,
  ])

  const handleMultiChoiceSelectionChange = useCallback((selection: Array<string>) => {
    setActiveMultiChoiceSelection(selection)
    focusChatInput?.()
  }, [focusChatInput])

  const handleRemoveTag = useCallback((tag: string) => {
    setActiveMultiChoiceSelection(prev => prev.filter(s => s !== tag))
    if (mobileSelectionText && tag === mobileSelectionText) {
      setMobileSelectionText(undefined)
    }
    if (tag === offerContextTag?.label) {
      setOfferContextTag(undefined)
    }
  }, [mobileSelectionText, offerContextTag])

  const submitDirect = useCallback(async(message: string, options?: OrderLinkOptions) => {
    setMessageInputValue('')
    setFollowTail(false)
    if (offerContextTag) setOfferContextTag(undefined)
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)
    return streamingSubmit(message, options)
  }, [streamingSubmit, setFollowTail, setMessageInputValue, offerContextTag])

  const resetSelectionState = useCallback(() => {
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)
    setOfferContextTag(undefined)
  }, [])

  return {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  }
}
